import { isLight, onThemeIcons, putImage } from "./theme";
import type { Map as MlMap, MapLayerMouseEvent } from "maplibre-gl";
import { dailyRainStats, RAIN_MODEL_IDS, thaiToday, type DailyRainStat } from "@flowsight/sim";
import { LEVEL, chipName } from "./rain-forecast";

/**
 * v0.9.6 Part A — 7-day rain forecast for every province, country view.
 * Data: /data/rain-fallback.json — the workflow's file (77 province reference points × 7 days × 5
 * Open-Meteo models, every 6 h + every deploy). ONE file, no extra Open-Meteo call from the browser.
 * Per day the same ensemble stats as the point card (dailyRainStats in @flowsight/sim: median,
 * min–max, TMD level, model agreement). Shapes: provinces-rain.geojson (OCHA, dissolved).
 *  A2  day bar (7 buttons) under the search box while no point is selected → tap a day: provinces
 *      filled by TMD level, translucent, under rivers / dams / staffs; low agreement ("faint") =
 *      lighter + hatched; tap the same day again = off
 *  A3  one line "ฝนหนัก N จังหวัด: …" (median ≥ ฝนหนัก) → tap: the list by amount
 *  A4  tap a province (while a day is on) → its 7-day card with update time; source and
 *      province reference-point details live in the About disclosure.
 * No file / a day without values → nothing is drawn for it (never a 0 or a guess).
 */
interface FilePoint { id: string; kind: string; name: string; lat: number; lon: number; daily: Record<string, unknown> }
interface RainFile { fetchedAt: string; source: string; models: string[]; points: FilePoint[] }
interface Prov { name: string; lat: number; lon: number; days: DailyRainStat[] }

const MODEL_NAME: Record<string, string> = {
  ecmwf_ifs025: "ECMWF IFS", gfs_seamless: "NCEP GFS", icon_seamless: "DWD ICON", jma_seamless: "JMA", ukmo_global_deterministic_10km: "UKMO Global",
};
const SRC = "prov-rain", FILL = "prov-rain-fill", HATCH = "prov-rain-hatch", LINE = "prov-rain-line";
const HEAVY = new Set(["heavy", "veryheavy"]);

let provs: Prov[] = [];
let fetchedAt = "";
let modelsPresent: string[] = [];
let day = -1;            // selected day index, -1 = off
let tlDay: number | null = null; // v0.9.6 B3: the ▶ timeline's day (overrides the bar while ▶ plays)
let suspended = false;   // a point is selected → the point's own rain UI takes over
let bar: HTMLElement | null = null;
let mapRef: MlMap | null = null;

export function provinceRainActive(): boolean { return day >= 0 && !suspended; }

export async function installProvinceRain(map: MlMap): Promise<void> {
  mapRef = map;
  let file: RainFile, shapes: GeoJSON.FeatureCollection;
  try {
    const [a, b] = await Promise.all([fetch("/data/rain-fallback.json"), fetch("/data/provinces-rain.geojson")]);
    if (!a.ok || !b.ok) return;
    file = (await a.json()) as RainFile;
    shapes = (await b.json()) as GeoJSON.FeatureCollection;
  } catch { return; }
  fetchedAt = file.fetchedAt;
  const pts = file.points.filter((p) => p.kind === "province");
  modelsPresent = RAIN_MODEL_IDS.filter((id) => pts.some((p) => ((p.daily[`precipitation_sum_${id}`] as (number | null)[] | undefined) ?? []).some((v) => v != null)));
  // the file is refreshed every 6 h → after midnight its first day can be yesterday: drop past days
  const today = thaiToday(new Date());
  provs = pts.map((p) => ({ name: p.name, lat: p.lat, lon: p.lon, days: dailyRainStats(p.daily, RAIN_MODEL_IDS).filter((d) => d.date >= today) }))
    .filter((p) => p.days.length);
  if (!provs.length) return;
  const source = document.getElementById("province-rain-source");
  if (source) {
    source.textContent = `ที่มา: Open-Meteo (${modelsPresent.map((m) => MODEL_NAME[m] ?? m).join(" · ")}) · จุดตัวแทนรายจังหวัด`;
    source.hidden = false;
  }
  const byName = new Map(provs.map((p) => [p.name, p]));
  // keep only shapes that have rain values; values ride on the feature (one property per day)
  const feats = shapes.features.filter((f) => byName.has(String(f.properties?.name))).map((f) => {
    const p = byName.get(String(f.properties!.name))!;
    const props: Record<string, unknown> = { name: p.name };
    p.days.forEach((d, i) => { if (d.m > 0) { props[`l${i}`] = d.level; props[`c${i}`] = d.confidence; } });
    return { ...f, properties: props };
  });

  putImage(map, "rain-hatch", hatchImage(), { pixelRatio: 2 });
  onThemeIcons(() => putImage(map, "rain-hatch", hatchImage(), { pixelRatio: 2 }));
  map.addSource(SRC, { type: "geojson", data: { type: "FeatureCollection", features: feats } });
  const below = map.getLayer("rivers-glow") ? "rivers-glow" : undefined; // under rivers, dams, staffs
  map.addLayer({ id: FILL, type: "fill", source: SRC, layout: { visibility: "none" }, paint: { "fill-color": "#000", "fill-opacity": 0 } }, below);
  map.addLayer({ id: HATCH, type: "fill", source: SRC, layout: { visibility: "none" }, paint: { "fill-pattern": "rain-hatch", "fill-opacity": 0.55 } }, below);
  map.addLayer({ id: LINE, type: "line", source: SRC, layout: { visibility: "none" }, paint: { "line-color": "#9fb3c8", "line-width": 0.6, "line-opacity": 0.35 } }, below);
  map.on("click", FILL, onProvinceTap);
  renderBar();
}

/** A point selected → hide the national bar + fill (the point's own rain chips/card take over). */
export function suspendProvinceRain(on: boolean): void {
  suspended = on;
  if (bar) bar.hidden = on;
  document.body.classList.toggle("has-prdays", !on && !!bar);
  publishHeight();
  paint();
}

function paint(): void {
  const m = mapRef; if (!m || !m.getLayer(FILL)) return;
  const d = tlDay ?? day;
  const vis = d >= 0 && (tlDay != null || !suspended) ? "visible" : "none";
  for (const id of [FILL, HATCH, LINE]) m.setLayoutProperty(id, "visibility", vis);
  if (vis === "none") return;
  const day_ = d;
  const lv = ["get", `l${day_}`], cf = ["get", `c${day_}`];
  const colour = ["match", lv, ...Object.entries(LEVEL).flatMap(([k, v]) => [k, v.c]), "#000"];
  m.setPaintProperty(FILL, "fill-color", colour as never);
  // translucent; "none" (no rain) barely tinted; low agreement lighter (the hatch marks it)
  m.setPaintProperty(FILL, "fill-opacity", ["case", ["!", ["has", `l${day_}`]], 0, ["==", lv, "none"], 0.04, ["==", cf, "faint"], 0.18, 0.34] as never);
  m.setFilter(HATCH, ["==", ["get", `c${day_}`], "faint"] as never);
  m.setFilter(LINE, ["has", `l${day_}`] as never);
}

function heavyOn(i: number): Prov[] {
  return provs.filter((p) => p.days[i] && p.days[i]!.m > 0 && HEAVY.has(p.days[i]!.level)).sort((a, b) => b.days[i]!.median - a.days[i]!.median);
}

function renderBar(): void {
  bar?.remove();
  const days = provs[0]!.days;
  bar = document.createElement("div");
  bar.className = "pr-days";
  bar.hidden = suspended;
  const btns = days.map((d, i) => `<button class="pr-day${i === day ? " sel" : ""}" data-i="${i}" aria-pressed="${i === day}">${chipName(d)}</button>`).join("");
  let line = "";
  if (day >= 0) {
    const h = heavyOn(day);
    line = h.length
      ? `<button class="pr-sum" aria-expanded="false">ฝนหนัก ${h.length} จังหวัด: ${h.slice(0, 3).map((p) => p.name).join(", ")}${h.length > 3 ? "…" : ""} ▾</button><div class="pr-list" hidden>${h.map((p) => `<div class="pr-li"><span>${p.name}</span><span>${p.days[day]!.median} มม. <span class="muted">(${p.days[day]!.min}–${p.days[day]!.max})</span></span></div>`).join("")}</div>`
      : `<div class="pr-sum none">ไม่มีจังหวัดฝนหนักตามพยากรณ์</div>`;
    line += `<div class="pr-src">อัปเดต ${fmt(fetchedAt)}</div>`;
  }
  bar.innerHTML = `<div class="pr-row" role="group" aria-label="ฝนพยากรณ์รายจังหวัด 7 วัน"><span class="pr-label">🌧 ฝน</span>${btns}</div>${line}<div class="pr-cardhost"></div>`;
  bar.querySelectorAll<HTMLElement>(".pr-day").forEach((b) => b.addEventListener("click", () => {
    const i = Number(b.dataset.i);
    day = day === i ? -1 : i; // tap the same day again = off
    renderBar(); paint();
  }));
  const sum = bar.querySelector<HTMLButtonElement>("button.pr-sum");
  sum?.addEventListener("click", () => {
    const list = bar!.querySelector<HTMLElement>(".pr-list"); if (!list) return;
    list.hidden = !list.hidden; sum.setAttribute("aria-expanded", String(!list.hidden));
    publishHeight();
  });
  document.body.append(bar);
  document.body.classList.toggle("has-prdays", !suspended);
  showCard();
  publishHeight();
}

/** Things below the bar (ⓘ on phones, จุดท่วมสูงสุด, the storm pill) move down by its height. */
function publishHeight(): void {
  // the province card is a transient overlay → not counted (ⓘ etc. stay right under the bar)
  const host = bar?.querySelector<HTMLElement>(".pr-cardhost");
  const h = bar && !bar.hidden ? Math.ceil(bar.getBoundingClientRect().height - (host?.getBoundingClientRect().height ?? 0)) + 6 : 0;
  document.documentElement.style.setProperty("--prdays-h", `${h}px`);
  fitCard();
  window.dispatchEvent(new Event("fs-top-layout")); // 🌀 / 〰 re-place now, not on their next tick
}

/** Keep the province card above 💧 / สรุป / ▶ and the phone sheet — it scrolls inside instead. */
function fitCard(): void {
  const card = bar?.querySelector<HTMLElement>(".pr-card");
  if (!card) return;
  const top = card.getBoundingClientRect().top;
  let limit = window.innerHeight;
  for (const q of ["#locate-fab", "#summary-fab", "#flow-fab", "#sos-fab", ".sheet"]) {
    const el = document.querySelector<HTMLElement>(q);
    if (!el || el.hidden || getComputedStyle(el).display === "none") continue;
    const r = el.getBoundingClientRect();
    if (r.height > 0 && r.top > top) limit = Math.min(limit, r.top);
  }
  card.style.maxHeight = `${Math.max(120, Math.floor(limit - top - 10))}px`;
}
window.addEventListener("resize", publishHeight);

let cardFor: string | null = null; // province whose card is open (kept across day changes)

function onProvinceTap(e: MapLayerMouseEvent): void {
  if (!provinceRainActive() || !mapRef) return;
  // a dam / staff / gauge under the finger opens its own card — not the province's
  const own = ["dams-layer", "dams-arrow", "tw-over", "tw-near", "tw-normal", "bkk-gauges", "bkk-gauges-label", "storm-cur-icon"].filter((l) => mapRef!.getLayer(l));
  if (own.length && mapRef.queryRenderedFeatures(e.point, { layers: own }).length) return;
  const name = String(e.features?.[0]?.properties?.name ?? "");
  if (!provs.some((x) => x.name === name)) return;
  cardFor = name;
  showCard();
}

/** The province card lives INSIDE the day bar (under the summary line) — a map popup would sit
 *  under the bar near the top of the screen. */
function showCard(): void {
  const host = bar?.querySelector<HTMLElement>(".pr-cardhost");
  if (!host) return;
  const p = cardFor ? provs.find((x) => x.name === cardFor) : null;
  if (!p || day < 0) { host.replaceChildren(); return; }
  host.innerHTML = provinceCard(p);
  host.querySelector(".pr-close")?.addEventListener("click", () => { cardFor = null; host.replaceChildren(); publishHeight(); });
  publishHeight();
}

function provinceCard(p: Prov): string {
  const maxV = Math.max(1, ...p.days.map((d) => d.max));
  const cols = p.days.map((d, i) => {
    if (d.m === 0) return `<div class="rf-day"><div class="rf-barwrap"></div><div class="rf-med">–</div><div class="rf-dow">${chipName(d)}</div></div>`;
    const col = LEVEL[d.level]?.c ?? LEVEL.none!.c;
    const h = Math.round((d.median / maxV) * 46) + 2, top = Math.round((d.max / maxV) * 46) + 2, bot = Math.round((d.min / maxV) * 46) + 2;
    return `<div class="rf-day conf-${d.confidence}${i === day ? " sel" : ""}" title="${chipName(d)}: ${d.min}–${d.max} มม. · ${d.heavyCount} จาก ${d.m} แบบจำลองเห็นว่าฝนหนัก">
      <div class="rf-barwrap"><div class="rf-range" style="bottom:${bot}px;height:${Math.max(1, top - bot)}px"></div><div class="rf-bar" style="height:${h}px;background:${col}"></div></div>
      <div class="rf-med">${d.median}</div><div class="rf-dow">${chipName(d)}</div></div>`;
  }).join("");
  const sel = p.days[day >= 0 ? day : 0]!;
  const selLine = sel.m === 0 ? "ไม่มีข้อมูลวันนี้"
    : `${chipName(sel)}: <b style="color:${LEVEL[sel.level]?.c}">${LEVEL[sel.level]?.th}</b> · ${sel.min === sel.max ? `${sel.median} มม.` : `${sel.min}–${sel.max} มม. (กลาง ${sel.median})`} · ${sel.heavyCount === 0 ? `ทั้ง ${sel.m} แบบจำลองเห็นตรงกันว่าไม่มีฝนหนัก` : `${sel.heavyCount} จาก ${sel.m} แบบจำลองเห็นว่าฝนหนัก`}`;
  return `<div class="rf-card pr-card"><button class="pr-close" aria-label="ปิดการ์ดจังหวัด">✕</button><div class="rf-head">🌧 ฝน 7 วัน · ${p.name === "กรุงเทพมหานคร" ? p.name : `จ.${p.name}`}</div>
    <div class="rf-row">${cols}</div><div class="small" style="margin-top:6px">${selLine}</div>
    <div class="muted small" style="margin-top:6px">อัปเดต ${fmt(fetchedAt)}</div></div>`;
}

function fmt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString("th-TH", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short", timeZone: "Asia/Bangkok" });
}

/** 16×16 (@2x → 8 px) diagonal stripes, white at low alpha — marks "faint" (low agreement). */
function hatchImage(): { width: number; height: number; data: Uint8Array } {
  const n = 16, data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    // white stripes on the dark map, slate stripes on the light one (v0.9.9 B5)
    if ((x + y) % 8 < 2) { const o = (y * n + x) * 4, [r, g, b, a] = isLight() ? [51, 65, 85, 120] : [255, 255, 255, 150]; data[o] = r; data[o + 1] = g; data[o + 2] = b; data[o + 3] = a; }
  }
  return { width: n, height: n, data };
}

/* ── v0.9.6 B3: the ▶ timeline drives the province fill by calendar date ─────── */
/** Dates covered by the forecast (today onward), or [] when the file is missing. */
export function rainTimelineDates(): string[] { return provs[0]?.days.map((d) => d.date) ?? []; }
/** Fill the provinces for that Thai date (null = hand back to the day bar). Returns the heavy-rain
 *  province names of that day (median ≥ ฝนหนัก, largest first) or null when the date is not covered. */
export function setTimelineRainDate(date: string | null): string[] | null {
  const i = date ? rainTimelineDates().indexOf(date) : -1;
  tlDay = date == null ? null : i;  // a date past the forecast → -1 (nothing drawn)
  paint();
  return i >= 0 ? heavyOn(i).map((p) => p.name) : null;
}
